/**
 * The growth domain's FLAG MODULE (ruling 36 as amended 2026-08-29).
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptors and the named getters
 * that read them are CO-LOCATED here, and `@heidi/growth/flags` publishes both.
 * `/contributions` carries nothing for flags — this domain HAS such a door,
 * holding analytics event names and a modal descriptor, and neither of the
 * declarations below belongs in it.
 *
 * ONE MINTING SITE PER FLAG, made structural rather than conventional by
 * co-location: each getter reads `descriptor.key` / `descriptor.defaultValue`
 * from the declaration directly above it, so the pair cannot drift, and
 * `flags.test.ts` asserts each key appears exactly once in this file and that no
 * read takes a string literal — the only assertion that can tell a descriptor
 * read from a hardcoded string that happens to be equal today. Flag keys have no
 * cross-site-identity guard (cache keys do), so a second spelling would be
 * invisible to every other gate in the repo.
 *
 * THE DOOR IS HEADLESS, guarded transitively rather than by inspection.
 * `scripts/monorepo-migration/lib/package-ui-zone.ts` admits
 * `@heidi/runtime/feature-flags` ONLY from this file out of everything under
 * `packages/growth/src`; `scripts/check-flag-getter-doors.test.ts` walks this
 * module's graph ACROSS package boundaries and refuses `@heidi/design-system`,
 * this package's own `/ui` zone and the app tree anywhere in it, and separately
 * proves the door imports in a bare Node process with no DOM and no side
 * effects.
 *
 * PACKAGE UI IMPORTS THESE RELATIVELY (`../../flags`), never through the door —
 * a package that imports itself by name resolves through node_modules and reads
 * as an undeclared external dependency.
 *
 * THIRTEEN GETTERS ARRIVED WITH THE WHOLE-CLASS FAN-OUT; eight remain. The
 * sample-conversation flag and its market/specialty operational toggles were
 * folded into the `experimental-2` arm of `release-new-onboarding-flow` (one
 * flag picks the whole onboarding flow), and the unread onboarding-v2 umbrella
 * was retired — see `docs/feature-flag-cleanups.md`.
 *
 * ONE OF THEM READS THE ENVIRONMENT, and it is not an env read in this package.
 * `useIsMicTestLiveTranscriptionEnabled` defaults ON in the DEV environment so
 * the flow is testable without configuring Flagsmith, and that branch is
 * behaviour rather than a force-all hatch, so it moved with the getter rather
 * than collapsing. It crosses on `getEnvironment()` from `@heidi/api/config` —
 * an already-declared platform door that holds the `NEXT_PUBLIC_ENVIRONMENT`
 * read, the same one `src/utils/runtime-wiring.ts` uses to configure the logger
 * — so `flags.ts` itself still names no `process.env` and `flags.test.ts`
 * asserts it.
 *
 * WHAT DOES NOT BELONG HERE. `useEffectiveSessionTimeout` is NOT a flag — it
 * merges `GET /user` with a user-scoped localStorage override and decides what
 * `AutoLogout` enforces — so it stays app-side and its value crosses into the
 * moved hooks as one added parameter. The two meddevice compliance-notification
 * keys (`release-scribe-notification-mfa-setup`,
 * `release-scribe-notification-inactivity-logout`) resolve through PostHog,
 * which this door carries no surface for; converting one would change WHICH
 * PROVIDER answers, and `monorepo-wave-1-p3-feature-flags` Z4 is the standing
 * rule against it. They live in
 * `src/features/flags/meddevice-compliance-notification-flags.ts` and the
 * resolved booleans cross into `ui/meddevice-compliance-notifications/` as
 * `flagGates`. And this domain's other app-barrel getters
 * (`useIsModalPacingEnabled`, `useExpandedSourceControlTrackingEnabled`,
 * `useIsOnboardingGuidedTourEnabled` and `useIsOnboardingResumeEnabled`) are
 * PostHog for the same reason.
 */
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { defineFeatureFlag, useBooleanFlag } from '@heidi/runtime/feature-flags';

// ---------------------------------------------------------------------------
// The declarations. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming one is switching flags, never a refactor.
//
// `builds` IS MEASURED PER DESCRIPTOR HERE, and this is the first flag module in
// the tree where the answer is not uniform. Six of the nine are `'all'`;
// the three Heidi Remote intro promos are `'cloud-only'` because their one
// eligibility hook short-circuits on the build profile —
// `src/features/growth/remote-intro-promo/eligibility/useIsRemoteIntroPromoEligible.ts`
// opens with `if (isOnPrem) return null`, so the surface cannot render on the
// appliance and claiming `'all'` for it would be a false record.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// THE WHOLE-CLASS FAN-OUT: the declarations and their getters.
//
// THE FORCE-ALL POSITION IS THE SAME FOR ALL OF THEM — a GAIN, not a collapse.
// Not one of the app-side bodies these replace carried a hand-rolled
// `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` branch, so each simply gains the
// injected `forceEnableAll` override that a raw `useBooleanFlagValue` bypassed.
// ---------------------------------------------------------------------------

/**
 * The simplified demo entry point — "Create a test session" header copy and no
 * StartButton on the entry-point overlay.
 */
export const demoFlowSimplificationFlag = defineFeatureFlag({
  key: 'release-scribe-enable-demo-flow-simplification',
  defaultValue: false,
  description: 'Simplified demo entry point',
  builds: 'all',
});

/**
 * The Heidi Remote intro promo for the MONTHLY Scribe-Plus/Clinician cohort. The CTA routes to the in-app annual-upgrade flow. The component layer
 * also checks the offer cutoff, so a stale flag cannot keep the promo visible.
 */
export const remoteIntroPromoMonthlyFlag = defineFeatureFlag({
  key: 'release-growth-remote-intro-promo-monthly',
  defaultValue: false,
  description: 'Heidi Remote intro promo for the monthly cohort',
  builds: 'cloud-only',
});

/**
 * The same promo for the YEARLY cohort (loyalty pricing), whose CTA routes to the
 * Heidi Remote storefront.
 */
export const remoteIntroPromoYearlyFlag = defineFeatureFlag({
  key: 'release-growth-remote-intro-promo-yearly',
  defaultValue: false,
  description: 'Heidi Remote intro promo for the yearly cohort',
  builds: 'cloud-only',
});

/**
 * The same promo for FREE and trialling users — the bundle, where a Remote
 * purchase includes one free month of Clinician.
 */
export const remoteIntroPromoFreeFlag = defineFeatureFlag({
  key: 'release-growth-remote-intro-promo-free',
  defaultValue: false,
  description: 'Heidi Remote intro promo for free and trialling users',
  builds: 'cloud-only',
});

/**
 * The onboarding compliance/safety copy: the "Intended use statement" accordion
 * and sticky CTA on the privacy step, and the documentation reminder on the
 * note-ready step. Defaults ON.
 */
export const onboardingComplianceCopyFlag = defineFeatureFlag({
  key: 'release-onboarding-compliance-copy',
  defaultValue: true,
  description: 'Onboarding compliance and safety copy',
  builds: 'all',
});

/**
 * The personalised "live transcription" treatment on the onboarding mic-test
 * step, which names the clinician's specialty in the heading before the sample
 * transcript plays. Rollout/kill gate, not an experiment — no
 * `useManualExposure`.
 */
export const micTestLiveTranscriptionFlag = defineFeatureFlag({
  key: 'release-onboarding-mic-test-live-transcription',
  defaultValue: false,
  description: 'Live-transcription treatment on the onboarding mic test',
  builds: 'all',
});

/**
 * The region-onboarding self-heal guard (`useRequireRegionGuard`). ON reroutes
 * authenticated users whose properties resolve with no region to
 * `/scribe/new-user`; OFF (default) leaves the guard inert, which is what makes
 * it an instant kill switch for the redirect.
 */
export const regionOnboardingGuardFlag = defineFeatureFlag({
  key: 'release-growth-region-onboarding-guard',
  defaultValue: false,
  description: 'Region-onboarding self-heal redirect guard',
  builds: 'all',
});

/**
 * Hides the "Document" card in the three-card "Create template" type picker,
 * leaving Note + PDF form. Default OFF = Document card shown.
 */
export const documentTemplateCardHiddenFlag = defineFeatureFlag({
  key: 'release-growth-hide-document-template-card',
  defaultValue: false,
  description: 'Hide the Document card in the create-template type picker',
  builds: 'all',
});

/** The simplified demo entry point. */
export const useIsDemoFlowSimplificationEnabled = (): boolean =>
  useBooleanFlag(demoFlowSimplificationFlag.key, demoFlowSimplificationFlag.defaultValue);

/** The Heidi Remote intro promo for the monthly cohort. */
export const useIsRemoteIntroPromoMonthlyEnabled = (): boolean =>
  useBooleanFlag(
    remoteIntroPromoMonthlyFlag.key,
    remoteIntroPromoMonthlyFlag.defaultValue
  );

/** The Heidi Remote intro promo for the yearly cohort. */
export const useIsRemoteIntroPromoYearlyEnabled = (): boolean =>
  useBooleanFlag(remoteIntroPromoYearlyFlag.key, remoteIntroPromoYearlyFlag.defaultValue);

/** The Heidi Remote intro promo for free and trialling users. */
export const useIsRemoteIntroPromoFreeEnabled = (): boolean =>
  useBooleanFlag(remoteIntroPromoFreeFlag.key, remoteIntroPromoFreeFlag.defaultValue);

/** The onboarding compliance and safety copy. */
export const useIsOnboardingComplianceCopyEnabled = (): boolean =>
  useBooleanFlag(
    onboardingComplianceCopyFlag.key,
    onboardingComplianceCopyFlag.defaultValue
  );

/**
 * The live-transcription treatment on the onboarding mic test.
 *
 * DEFAULTS ON IN THE DEV ENVIRONMENT, which is behaviour rather than a force-all
 * hatch: Flagsmith targeting is not configured for local and dev deploys, and
 * PROD follows the Flagsmith default until the flag is ramped. The read crosses
 * on `@heidi/api/config`, so no `process.env` appears in this module.
 */
export const useIsMicTestLiveTranscriptionEnabled = (): boolean => {
  const flag = useBooleanFlag(
    micTestLiveTranscriptionFlag.key,
    micTestLiveTranscriptionFlag.defaultValue
  );
  return getEnvironment() === ENV_NAME.DEV || flag;
};

/** The region-onboarding self-heal redirect guard. */
export const useIsRegionOnboardingGuardEnabled = (): boolean =>
  useBooleanFlag(regionOnboardingGuardFlag.key, regionOnboardingGuardFlag.defaultValue);

/** Whether the Document card is hidden in the create-template type picker. */
export const useIsDocumentTemplateCardHidden = (): boolean =>
  useBooleanFlag(
    documentTemplateCardHiddenFlag.key,
    documentTemplateCardHiddenFlag.defaultValue
  );
